"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";

interface DataTableWrapperProps {
  title: string;
  description?: string;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  pagination?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

function DataTableSkeleton() {
  return (
    <div className="space-y-3 px-6 py-4">
      {/* Header row skeleton */}
      <div className="flex gap-4">
        <Skeleton className="h-4 w-[30%]" />
        <Skeleton className="h-4 w-[20%]" />
        <Skeleton className="h-4 w-[25%]" />
        <Skeleton className="h-4 w-[15%]" />
      </div>
      {/* Data row skeletons */}
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-8 w-[30%]" />
          <Skeleton className="h-8 w-[20%]" />
          <Skeleton className="h-8 w-[25%]" />
          <Skeleton className="h-8 w-[15%]" />
        </div>
      ))}
    </div>
  );
}

export function DataTableWrapper({
  title,
  description,
  toolbar,
  children,
  pagination,
  isLoading = false,
  className,
}: DataTableWrapperProps) {
  return (
    <div
      data-slot="data-table-wrapper"
      className={cn(
        "flex flex-col gap-4 rounded-lg border bg-card text-card-foreground",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-1.5 px-6 pt-6">
        <h2 className="text-lg font-semibold leading-none">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {/* Toolbar (search, filters, etc.) */}
      {toolbar && <div className="px-6">{toolbar}</div>}

      {/* Table content */}
      <div className="overflow-x-auto [&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:last-child]:pr-6">
        {isLoading ? <DataTableSkeleton /> : children}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="border-t px-6 py-4">{pagination}</div>
      )}
    </div>
  );
}
